/**
 * useOverlayPortal — Hook to access the AI Farm overlay portal container.
 *
 * Returns the DOM element with id="aif-overlay-root" where overlays should
 * render via ReactDOM.createPortal.
 *
 * Radix primitives accept a `container` prop on their Portal sub-components.
 * Use this hook's return value as that container to ensure all overlays
 * render in the dedicated AI Farm portal root.
 *
 * Example:
 *   const portalContainer = useOverlayPortal();
 *   <Dialog.Portal container={portalContainer}>...</Dialog.Portal>
 *
 * See: docs/architecture/OVERLAY_ARCHITECTURE.md §2.1, §7.3
 */

import { useEffect, useState } from 'react';

const PORTAL_ID = 'aif-overlay-root';

export function useOverlayPortal() {
  const [container, setContainer] = useState(null);

  useEffect(() => {
    // Defensive: if the portal root is missing from index.html (shouldn't
    // happen in production), create it dynamically as a fallback.
    let element = document.getElementById(PORTAL_ID);
    if (!element) {
      console.warn(
        `[OverlayProvider] Portal root #${PORTAL_ID} not found in DOM. ` +
        `Creating dynamically. Add <div id="${PORTAL_ID}"></div> to index.html.`
      );
      element = document.createElement('div');
      element.id = PORTAL_ID;
      document.body.appendChild(element);
    }
    setContainer(element);
  }, []);

  return container;
}
